/**
 * Point coordinates for Bezier control handles
 */
export interface HandlePoint {
  /** X offset relative to anchor point */
  x: number;
  /** Y offset relative to anchor point */
  y: number;
}

/**
 * Anchor point with Bezier control handles
 * Handles are stored as offsets relative to the anchor point position
 */
export interface AnchorPoint {
  /** X position in world coordinates */
  x: number;
  /** Y position in world coordinates */
  y: number;
  /** Incoming handle (controls curve arriving at this point) */
  handleIn: HandlePoint | null;
  /** Outgoing handle (controls curve leaving this point) */
  handleOut: HandlePoint | null;
  /** Handle mode: smooth = handles are collinear, corner = independent */
  handleMode: 'smooth' | 'corner';
}

/**
 * Line cap style for stroke endpoints
 */
export type LineCap = 'butt' | 'round' | 'square';

/**
 * Line join style for stroke corners
 */
export type LineJoin = 'miter' | 'round' | 'bevel';

/**
 * Arrow/marker type for path endpoints
 */
export type ArrowType = 'none' | 'arrow' | 'triangle' | 'circle' | 'square' | 'diamond';

/**
 * Dash pattern preset
 */
export type DashPreset = 'solid' | 'dashed' | 'dotted';

/**
 * Dash pattern values for each preset (stroke width multipliers)
 */
export const DASH_PATTERNS: Record<DashPreset, number[] | null> = {
  solid: null,
  dashed: [3, 2],     // 3x stroke width dash, 2x stroke width gap
  dotted: [1, 1.5]    // 1x stroke width dot, 1.5x stroke width gap
};

/**
 * Pen path instance placed on the map
 */
export interface PenPathInstance {
  /** Unique identifier */
  id: string;
  /** Array of anchor points defining the path */
  points: AnchorPoint[];
  /** Whether the path is closed (forms a shape) */
  closed: boolean;

  // Stroke style
  /** Stroke color in hex format, null for no stroke */
  strokeColor: string | null;
  /** Stroke width in pixels */
  strokeWidth: number;
  /** Stroke opacity (0-1) */
  strokeOpacity: number;
  /** Line cap style */
  lineCap: LineCap;
  /** Line join style */
  lineJoin: LineJoin;
  /** Dash pattern preset */
  dashPreset: DashPreset;

  // Arrow markers
  /** Arrow type at path start */
  arrowStart: ArrowType;
  /** Arrow type at path end */
  arrowEnd: ArrowType;

  // Fill style
  /** Fill color in hex format, null for no fill */
  fillColor: string | null;
  /** Fill opacity (0-1) */
  fillOpacity: number;
  /** Feather radius for soft edges on fill (0 = sharp edges) */
  featherRadius: number;
  /** Invert fill - fills everything except the path (cutout effect) */
  invertFill: boolean;

  // Position and transformation
  /** X position of path center in world coordinates */
  x: number;
  /** Y position of path center in world coordinates */
  y: number;
  /** Rotation in degrees */
  rotation: number;
  /** Uniform scale factor */
  scale: number;

  // Metadata
  /** Z-index for rendering order */
  zIndex: number;
  /** Layer ID this path belongs to */
  layerId: string;
  /** Region X coordinate (calculated from position) */
  regionX?: number;
  /** Region Y coordinate (calculated from position) */
  regionY?: number;
}

/**
 * Settings for the pen tool
 */
export interface PenToolSettings {
  /** Default stroke color */
  strokeColor: string;
  /** Default stroke width */
  strokeWidth: number;
  /** Default stroke opacity */
  strokeOpacity: number;
  /** Default line cap */
  lineCap: LineCap;
  /** Default line join */
  lineJoin: LineJoin;
  /** Default dash preset */
  dashPreset: DashPreset;
  /** Default arrow at start */
  arrowStart: ArrowType;
  /** Default arrow at end */
  arrowEnd: ArrowType;
  /** Default fill color (null for no fill) */
  fillColor: string | null;
  /** Default fill opacity */
  fillOpacity: number;
  /** Default feather radius for soft edges (0 = sharp edges) */
  featherRadius: number;
  /** Default invert fill setting */
  invertFill: boolean;
}

/**
 * Default pen tool settings
 */
export const DEFAULT_PEN_TOOL_SETTINGS: PenToolSettings = {
  strokeColor: '#000000',
  strokeWidth: 2,
  strokeOpacity: 1,
  lineCap: 'round',
  lineJoin: 'round',
  dashPreset: 'solid',
  arrowStart: 'none',
  arrowEnd: 'none',
  fillColor: null,
  fillOpacity: 1,
  featherRadius: 0,
  invertFill: false
};

/**
 * Region data for pen paths
 */
export interface RegionPenPaths {
  /** Region X coordinate */
  regionX: number;
  /** Region Y coordinate */
  regionY: number;
  /** List of pen paths in this region */
  penPaths: PenPathInstance[];
}

/**
 * Calculates the center point of a path from its anchor points
 * @param points Array of anchor points
 * @returns Center coordinates
 */
export function calculatePathCenter(points: AnchorPoint[]): { x: number; y: number } {
  if (points.length === 0) {
    return { x: 0, y: 0 };
  }

  let sumX = 0;
  let sumY = 0;

  for (const point of points) {
    sumX += point.x;
    sumY += point.y;
  }

  return {
    x: sumX / points.length,
    y: sumY / points.length
  };
}

/**
 * Calculates the bounding box of a path
 * @param points Array of anchor points
 * @returns Bounding box coordinates
 */
export function calculatePathBounds(points: AnchorPoint[]): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
} {
  if (points.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const point of points) {
    // Include anchor point
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);

    // Include control handles (they affect the curve bounds)
    if (point.handleIn) {
      const hx = point.x + point.handleIn.x;
      const hy = point.y + point.handleIn.y;
      minX = Math.min(minX, hx);
      minY = Math.min(minY, hy);
      maxX = Math.max(maxX, hx);
      maxY = Math.max(maxY, hy);
    }
    if (point.handleOut) {
      const hx = point.x + point.handleOut.x;
      const hy = point.y + point.handleOut.y;
      minX = Math.min(minX, hx);
      minY = Math.min(minY, hy);
      maxX = Math.max(maxX, hx);
      maxY = Math.max(maxY, hy);
    }
  }

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY
  };
}

/**
 * Subdivides a cubic Bezier curve into line segments for hit testing
 * @param p0 Start point
 * @param p1 First control point
 * @param p2 Second control point
 * @param p3 End point
 * @param segments Number of segments to create
 * @returns Array of points along the curve
 */
export function subdivideBezier(
  p0: { x: number; y: number },
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  p3: { x: number; y: number },
  segments: number = 20
): { x: number; y: number }[] {
  const points: { x: number; y: number }[] = [];

  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const t2 = t * t;
    const t3 = t2 * t;
    const mt = 1 - t;
    const mt2 = mt * mt;
    const mt3 = mt2 * mt;

    points.push({
      x: mt3 * p0.x + 3 * mt2 * t * p1.x + 3 * mt * t2 * p2.x + t3 * p3.x,
      y: mt3 * p0.y + 3 * mt2 * t * p1.y + 3 * mt * t2 * p2.y + t3 * p3.y
    });
  }

  return points;
}

/**
 * Calculates the distance from a point to a line segment
 * @param px Point X
 * @param py Point Y
 * @param x1 Segment start X
 * @param y1 Segment start Y
 * @param x2 Segment end X
 * @param y2 Segment end Y
 * @returns Distance to the segment
 */
export function distanceToSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) {
    // Segment is a point
    return Math.sqrt((px - x1) * (px - x1) + (py - y1) * (py - y1));
  }

  // Project point onto line, clamped to segment
  let t = ((px - x1) * dx + (py - y1) * dy) / lengthSquared;
  t = Math.max(0, Math.min(1, t));

  const projX = x1 + t * dx;
  const projY = y1 + t * dy;

  return Math.sqrt((px - projX) * (px - projX) + (py - projY) * (py - projY));
}

/**
 * Calculates the tangent angle at the start of a path segment
 * @param p0 Start anchor point
 * @param p1 End anchor point
 * @returns Angle in radians
 */
export function getStartTangentAngle(p0: AnchorPoint, p1: AnchorPoint): number {
  let dx: number;
  let dy: number;

  if (p0.handleOut) {
    // Use the outgoing handle direction
    dx = p0.handleOut.x;
    dy = p0.handleOut.y;
  } else if (p1.handleIn) {
    // Use direction to the incoming handle of next point
    dx = (p1.x + p1.handleIn.x) - p0.x;
    dy = (p1.y + p1.handleIn.y) - p0.y;
  } else {
    // Straight line to next point
    dx = p1.x - p0.x;
    dy = p1.y - p0.y;
  }

  return Math.atan2(dy, dx);
}

/**
 * Calculates the tangent angle at the end of a path segment
 * @param p0 Start anchor point
 * @param p1 End anchor point
 * @returns Angle in radians
 */
export function getEndTangentAngle(p0: AnchorPoint, p1: AnchorPoint): number {
  let dx: number;
  let dy: number;

  if (p1.handleIn) {
    // Use the incoming handle direction (reversed)
    dx = -p1.handleIn.x;
    dy = -p1.handleIn.y;
  } else if (p0.handleOut) {
    // Use direction from the outgoing handle of previous point
    dx = p1.x - (p0.x + p0.handleOut.x);
    dy = p1.y - (p0.y + p0.handleOut.y);
  } else {
    // Straight line from previous point
    dx = p1.x - p0.x;
    dy = p1.y - p0.y;
  }

  return Math.atan2(dy, dx);
}
